import { useTranslation } from "react-i18next";
import { Combobox, type ComboboxOption } from "@tellescope/ui";
import { useMemo } from "react";
import { levenshteinDistance, US_STATES, USStateCode } from "@tellescope/utils";

export interface StateSelectFieldProps {
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  id?: string;
  statesToExclude?: USStateCode[];
  validStates?: string[];
  placeholder?: string;
  autoFocus?: boolean;
}

export const StateSelectField = ({
  value,
  onValueChange,
  className,
  id,
  statesToExclude = [],
  validStates,
  placeholder,
  autoFocus,
}: StateSelectFieldProps) => {
  const { t } = useTranslation("forms");

  const allowedStates = useMemo(() => {
    const allowed =
      validStates && validStates.length > 0 ? US_STATES.filter((s) => validStates.includes(s.value)) : US_STATES;
    return statesToExclude.length ? allowed.filter((s) => !statesToExclude.includes(s.value)) : allowed;
  }, [validStates, statesToExclude]);

  // Labels are resolved outside the memos so a language change re-translates them on re-render.
  const stateOptions = allowedStates.map((s) => ({ value: s.value, label: s.getLabel() }));

  // Resolved against the unfiltered list so an already-saved state still displays even
  // when statesToExclude or validStates keeps it out of the selectable options.
  const selectedState = useMemo(() => US_STATES.find((option) => option.value === value), [value]);
  const selectedOption = selectedState && { value: selectedState.value, label: selectedState.getLabel() };

  // Handle change by extracting the value from the ComboboxOption
  const handleChange = (option: ComboboxOption | undefined) => {
    onValueChange?.(option?.value ?? "");
  };

  return (
    <Combobox
      value={selectedOption}
      onChange={handleChange}
      options={stateOptions}
      placeholder={placeholder ?? t("Select a state")}
      searchPlaceholder={t("Search states...")}
      className={className}
      openOnFocus
      autoFocus={autoFocus}
      customFilter={(searchTerm, options) => {
        return options
          .filter((option) =>
            `${option.label.toLowerCase()}|${option.value.toLowerCase()}`.includes(searchTerm.toLowerCase())
          )
          .sort((a, b) => {
            const lowerSearch = searchTerm.toLowerCase();
            const aMinDistance = Math.min(
              levenshteinDistance(lowerSearch, a.label.toLowerCase()),
              levenshteinDistance(lowerSearch, a.value.toLowerCase())
            );
            const bMinDistance = Math.min(
              levenshteinDistance(lowerSearch, b.label.toLowerCase()),
              levenshteinDistance(lowerSearch, b.value.toLowerCase())
            );
            return aMinDistance - bMinDistance;
          });
      }}
      id={id}
    />
  );
};
